import { Button } from "@d12labs/ui/button";
import { formatClock } from "@d12labs/utils";
import { Download, Mic, Video } from "lucide-react";
import { type RefObject, useRef } from "react";
import type { Recording, SpeakerTurn } from "../types";
import { Waveform } from "./waveform";

interface SpeakerMeta {
	name: string;
	colorIndex: number;
}

interface RecordingPanelProps {
	recordings: Recording[];
	turns: SpeakerTurn[];
	speakerMeta: Map<string, SpeakerMeta>;
	playhead: number;
	onPlayheadChange: (v: number) => void;
	onRefetch: () => void;
	fetching?: boolean;
	/** Owned by the page so quote/annotation clicks can seek even when collapsed. */
	audioRef: RefObject<HTMLAudioElement | null>;
	/** Live audio playing state, driven by the audio element's play/pause events. */
	isPlaying: boolean;
}

function formatDuration(sec: number | null): string {
	return sec == null ? "—" : formatClock(sec);
}

function formatBytes(bytes: number | null): string {
	if (bytes == null) return "";
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ACCENT_COLORS: [string, string] = ["var(--color-primary)", "var(--color-success)"];

function PlayPauseIcon({ playing }: { playing: boolean }) {
	if (playing) {
		return (
			<svg viewBox="0 0 12 12" className="size-3" fill="currentColor" aria-hidden="true">
				<rect x="3" y="2" width="2.5" height="8" rx="0.5" />
				<rect x="6.5" y="2" width="2.5" height="8" rx="0.5" />
			</svg>
		);
	}
	return (
		<svg viewBox="0 0 12 12" className="ml-0.5 size-3" fill="currentColor" aria-hidden="true">
			<path d="M3 1.5v9l8-4.5L3 1.5Z" />
		</svg>
	);
}

export function RecordingPanel({
	recordings,
	turns,
	speakerMeta,
	playhead,
	audioRef,
	isPlaying,
}: RecordingPanelProps) {
	const audioRec = recordings.find((r) => r.kind === "audio" && r.status === "stored");
	const videoRec = recordings.find((r) => r.kind === "video" && r.status === "stored");

	// Video is rendered inline with native <video controls> (so the player "opens
	// up" like before and gives the user a real scrub bar + fullscreen for free).
	// We own it locally; pausing audio when the user hits play is wired below.
	const videoRef = useRef<HTMLVideoElement>(null);

	// Speaker legend (first two by encounter order)
	const speakerEntries = Array.from(speakerMeta.entries()).slice(0, 2);

	// Per-speaker airtime % from turns (the same source the waveform colors use).
	const totalSec = turns.reduce((sum, t) => sum + (t.endSec - t.startSec), 0) || 1;
	const speakerSec = new Map<string, number>();
	for (const t of turns) {
		speakerSec.set(t.speakerId, (speakerSec.get(t.speakerId) ?? 0) + (t.endSec - t.startSec));
	}

	const audioDur = audioRec?.durationSec ?? null;
	const currentSec = audioDur != null ? playhead * audioDur : playhead * totalSec;

	// Click-to-seek on the waveform: map pixel position → audio.currentTime.
	const handleWaveformSeek = (e: React.MouseEvent<HTMLButtonElement>) => {
		const a = audioRef.current;
		if (!a || !Number.isFinite(a.duration) || a.duration <= 0) return;
		const rect = e.currentTarget.getBoundingClientRect();
		const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
		a.currentTime = ratio * a.duration;
	};

	return (
		<div className="space-y-3 border-b border-hairline bg-surface-1/40 px-6 py-4">
			{/* Audio row — compact strip with the play toggle. */}
			<div className="flex items-center gap-3 rounded-md border border-hairline bg-surface-1 px-3 py-2">
				<button
					type="button"
					className="flex size-7 flex-none items-center justify-center rounded-full bg-ink text-canvas hover:bg-ink-muted disabled:opacity-40"
					disabled={!audioRec?.url}
					onClick={() => {
						const a = audioRef.current;
						if (!a) return;
						if (a.paused) {
							// Pause video first so they don't double-play.
							const v = videoRef.current;
							if (v && !v.paused) v.pause();
							void a.play();
						} else {
							a.pause();
						}
					}}
					title={isPlaying ? "Pause audio" : "Play audio"}
				>
					<PlayPauseIcon playing={isPlaying} />
				</button>
				<Mic className="size-3.5 flex-none text-ink-subtle" />
				<span className="text-body-sm text-ink">Audio</span>
				<span className="text-meta text-ink-subtle">
					{audioRec
						? `${formatDuration(audioRec.durationSec)} · ${formatBytes(audioRec.sizeBytes)} · Fireflies`
						: "No audio recording"}
				</span>
				<span className="ml-auto font-mono text-meta tabular-nums text-ink-subtle">
					{formatDuration(currentSec)} / {formatDuration(audioDur)}
				</span>
				{audioRec?.url ? (
					<Button variant="ghost" size="icon-sm" asChild>
						<a href={audioRec.url} download>
							<Download className="size-3.5" />
						</a>
					</Button>
				) : null}
			</div>

			{/* Dual-speaker waveform — click anywhere to seek the audio. */}
			<div className="rounded-md border border-hairline bg-surface-1 p-3">
				<div className="mb-2 flex items-center gap-4 text-body-sm">
					{speakerEntries.map(([sid, meta], idx) => {
						const pct = Math.round(((speakerSec.get(sid) ?? 0) / totalSec) * 100);
						return (
							<span key={sid} className="flex items-center gap-1.5 text-ink-muted">
								<span
									className="size-2 flex-none rounded-full"
									style={{ background: ACCENT_COLORS[idx] ?? "var(--color-primary)" }}
								/>
								{meta.name} · {pct}%
							</span>
						);
					})}
				</div>
				<button
					type="button"
					onClick={handleWaveformSeek}
					className="block w-full cursor-pointer"
					aria-label="Seek audio"
					disabled={!audioRec?.url}
				>
					<Waveform height={64} bars={180} playhead={playhead} accent={ACCENT_COLORS} />
				</button>
			</div>

			{/* Video — inline native <video controls> so the player opens up and
			    the timeline is scrub-able like before. */}
			<div className="rounded-md border border-hairline bg-surface-1">
				<div className="flex items-center gap-3 border-b border-hairline px-3 py-2">
					<Video className="size-3.5 flex-none text-ink-subtle" />
					<span className="text-body-sm text-ink">Video</span>
					<span className="text-meta text-ink-subtle">
						{videoRec
							? `${formatDuration(videoRec.durationSec)} · ${formatBytes(videoRec.sizeBytes)} · Fireflies`
							: "No video recording"}
					</span>
					<span className="ml-auto font-mono text-meta tabular-nums text-ink-subtle">
						{formatDuration(videoRec?.durationSec ?? null)}
					</span>
					{videoRec?.url ? (
						<Button variant="ghost" size="icon-sm" asChild>
							<a href={videoRec.url} download>
								<Download className="size-3.5" />
							</a>
						</Button>
					) : null}
				</div>
				{videoRec?.url ? (
					// biome-ignore lint/a11y/useMediaCaption: meeting recording has no caption track
					<video
						ref={videoRef}
						src={videoRec.url}
						controls
						playsInline
						preload="metadata"
						onPlay={() => {
							// Don't double-play with the audio element.
							const a = audioRef.current;
							if (a && !a.paused) a.pause();
						}}
						className="block max-h-[420px] w-full rounded-b-md bg-black"
					/>
				) : (
					<div className="px-3 py-6 text-center text-caption text-ink-subtle">
						No video recording available.
					</div>
				)}
			</div>
		</div>
	);
}
